import { FY_MONTH_NAMES, formatMoney, type MonthFigure } from "./api";

/**
 * The two visual encodings this app uses, and only two.
 *
 * Neither is a categorical chart: the sparkline is one series, and the runway
 * indicator is a *status*, not a series. So there is no categorical palette to
 * validate, and no legend to draw — a single series is named by its heading.
 *
 * Status colour never carries meaning alone. The runway pill shows a word and
 * a figure beside the colour, because the warning and serious steps sit below
 * 3:1 on a light surface by design, and because a reader who cannot separate
 * the hues still has to be able to read it.
 */

/**
 * Twelve months of running position, with the zero line drawn.
 *
 * The zero line is the whole point: the reports exist because a month-end
 * level cannot show a crossing, and the eye finds a crossing far faster than
 * it finds a sign change in a column of figures.
 *
 * Values become numbers here to compute pixel positions. That is geometry, not
 * arithmetic on money — nothing derived from these numbers is ever displayed.
 */
export function PositionSparkline({
  months,
  width = 220,
  height = 44,
}: {
  months: MonthFigure[];
  width?: number;
  height?: number;
}) {
  const points = months
    .filter((month) => month.position !== undefined)
    .map((month) => ({ fyMonth: month.fyMonth, value: Number(month.position) }));

  if (points.length < 2) return <span className="muted">—</span>;

  const values = points.map((point) => point.value);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);
  const span = max - min || 1;
  const padding = 3;
  const plotHeight = height - padding * 2;

  const x = (index: number) => (index / (points.length - 1)) * (width - 2) + 1;
  const y = (value: number) => padding + (1 - (value - min) / span) * plotHeight;

  const path = points.map((point, index) => `${index === 0 ? "M" : "L"}${x(index).toFixed(1)},${y(point.value).toFixed(1)}`).join(" ");
  const zeroY = y(0);
  const last = points[points.length - 1]!;
  const negative = last.value < 0;

  return (
    <svg
      className="sparkline"
      // No width attribute: the viewBox plus `width: 100%` in CSS makes it
      // scale to the card. A fixed 520 was drawing past the right edge on a
      // phone, which is what the screenshot showed.
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Running position, ${FY_MONTH_NAMES[points[0]!.fyMonth - 1]} to ${
        FY_MONTH_NAMES[last.fyMonth - 1]
      }, ending ${formatMoney(String(last.value.toFixed(2)))}`}
    >
      <line
        x1={0}
        x2={width}
        y1={zeroY}
        y2={zeroY}
        className="sparkline-zero"
        strokeDasharray="2 3"
      />
      <path d={path} className="sparkline-line" fill="none" strokeWidth={2} />
      <circle
        cx={x(points.length - 1)}
        cy={y(last.value)}
        r={3.5}
        className={negative ? "sparkline-end negative" : "sparkline-end"}
      />
    </svg>
  );
}

/**
 * Months of equity as a runway: how long this balance lasts at the current
 * target. A ratio, so it is a status rather than a magnitude — and the bands
 * are the ones a person would act on, not equal slices of a range.
 */
export function RunwayPill({ months }: { months: string | undefined }) {
  if (months === undefined) return <span className="muted">no target</span>;
  const value = Number(months);
  const { level, word } =
    value < 0
      ? { level: "critical", word: "in deficit" }
      : value < 1
        ? { level: "serious", word: "under a month" }
        : value < 3
          ? { level: "warning", word: `${value.toFixed(1)} months` }
          : { level: "good", word: `${value.toFixed(1)} months` };

  return (
    <span className={`pill pill-${level}`}>
      <span className="pill-dot" aria-hidden="true" />
      {word}
    </span>
  );
}

/** A figure that reads as good or bad by sign, with the sign spelled out. */
export function Figure({ value, plain = false }: { value: string | undefined; plain?: boolean }) {
  if (value === undefined) return <span className="muted">—</span>;
  const negative = value.startsWith("-");
  return (
    <span className={plain ? "figure" : negative ? "figure negative" : "figure"}>
      {formatMoney(value)}
    </span>
  );
}
